import {
  MeterReadingsFilters,
  UtilityConfigFilters,
} from "@features/cx/shared/types/consumerRequest";
import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { queryClient } from "@shared/api/queries/queryClient";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { toast, useToast } from "@shared/hooks/use-toast";
import { downloadCSV, printPDFBlob } from "@shared/utils/utils";
import { useNavigate } from "react-router-dom";
import {
  approveBill,
  approveDiscoonnectionBill,
  disconnectionApi,
  downloadBillTemplate,
  getMeterHistory,
  getMeterInfo,
  getWorkOrderDetails,
  rejectBill,
  submitConsumerSelection,
  updateDisconnection,
} from "./api";
import {
  ConsumerBillFilters,
  ConsumerSearchFilters,
  ConsumerSupportDisconnectPayload,
  CreateBillGenerationPayload,
  CreateDisconnectionRequestPayload,
  DisconnectionFilters,
  SingleBillApprovePayload,
  UpdateBillGenerationPayload,
  UpdateDisconnectionRequestPayload,
} from "./types";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

// ================================
// CONSUMER SEARCH HOOKS
// ================================

/**
 * Search consumers for disconnection
 */
export const useConsumerSearch = (filters: ConsumerSearchFilters) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.disconnectionRequests.consumerSearch(filters),
    () => disconnectionApi.searchConsumer(filters),
    {
      enabled: filters.searchData.length >= 3,
      staleTime: 5 * 60 * 1000,
      retry: false,
    }
  );
};

/**
 * Get consumer meta data
 */
export const useConsumerMetaData = (params: {
  consumerId: number;
  remoteUtilityId: number;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.disconnectionRequests.consumerMetaData(params),
    () => disconnectionApi.getConsumerMetaData(params),
    {
      enabled: !!params.consumerId,
      staleTime: 5 * 60 * 1000,
    }
  );
};

/**
 * Get consumer bill data
 */
export const useConsumerBill = (filters: ConsumerBillFilters) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.disconnectionRequests.consumerBill(filters),
    () => disconnectionApi.getConsumerBill(filters),
    {
      enabled: !!filters.remoteUtilityId && !!filters.consumerId,
      staleTime: 2 * 60 * 1000,
    }
  );
};

/**
 * Get disconnection requests list
 */
export const useDisconnectionRequests = (filters: DisconnectionFilters) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.disconnectionRequests.list(filters),
    () => disconnectionApi.getDisconnectionRequests(filters),
    {
      staleTime: 2 * 60 * 1000,
    }
  );
};

export const useDisconnectionExport = (filters: DisconnectionFilters) => {
  const [isExporting, setIsExporting] = useState(false);

  const exportMutation = useSmartQuery(
    QueryKeyFactory.module.cx.disconnectionRequests.exportList(filters), // Adjust this query key based on your factory structure
    async () => {
      setIsExporting(true);
      try {
        const blob = await disconnectionApi.exportDisconnectionRequests(
          filters
        ); // Adjust API object name if needed

        toast({
          title: "Export Initiated",
          description: blob?.message,
          variant: "default",
        });

        return { success: true };
      } catch (error: any) {
        toast({
          title: `Failed to export disconnection requests: ${error.message}`,
          variant: "destructive",
        });
        throw error;
      } finally {
        setIsExporting(false);
      }
    },
    {
      enabled: false,
    }
  );

  const exportCSV = () => {
    exportMutation.refetch();
  };

  return {
    exportCSV,
    isExporting: isExporting || exportMutation.isFetching,
    error: exportMutation.error,
    isError: exportMutation.isError,
  };
};

/**
 * Get disconnection request detail
 */
export const useDisconnectionRequestDetail = (
  requestId: number,
  remoteUtilityId: string
) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.disconnectionRequests.details(String(requestId)),
    () =>
      disconnectionApi.getDisconnectionRequestDetail(
        requestId,
        remoteUtilityId
      ),
    {
      enabled: !!requestId && !!remoteUtilityId,
      staleTime: 2 * 60 * 1000,
    }
  );
};

/**
 * Get disconnection summary
 */
export const useDisconnectionSummary = (params: {
  remoteUtilityId: number;
  requestType: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.disconnectionRequests.summary(params),
    () => disconnectionApi.getDisconnectionSummary(params),
    {
      enabled: !!params.remoteUtilityId,
      staleTime: 5 * 60 * 1000,
    }
  );
};

// ================================
// DISCONNECTION MUTATION HOOKS
// ================================

/**
 * Create disconnection request
 */
export const useCreateDisconnectionRequest = () => {
  const navigate = useNavigate();
  return useSmartMutation(
    (payload: CreateDisconnectionRequestPayload) =>
      disconnectionApi.createDisconnectionRequest(payload),
    {
      invalidateKeys: [
        QueryKeyFactory.module.cx.disconnectionRequests.list(),
        QueryKeyFactory.module.cx.disconnectionRequests.summary(),
      ],
      onSuccess: (data: any, variables: any) => {
        toast({
          title: "Disconnection request created successfully!",
          variant: "default",
        });
        navigate("/cx/disconnections/success", {
          state: { ...data, formData: variables },
        });
      },
      onError: (error: any) => {
        toast({
          title: `Failed to create disconnection request: ${error.message}`,
          variant: "destructive",
        });
      },
    }
  );
};

/**
 * Update disconnection request
 */
// export const useUpdateDisconnectionRequest = (requestId: number) => {
//   return useSmartMutation(
//     ({
//       requestId,
//       payload,
//     }: {
//       requestId: number;
//       payload: UpdateDisconnectionRequestPayload;
//     }) => disconnectionApi.updateDisconnectionRequest(requestId, payload),
//     {
//       invalidateKeys: [

//         QueryKeyFactory.module.cx.disconnectionRequests.details(
//           String(requestId)
//         ),
//         QueryKeyFactory.module.cx.disconnectionRequests.list(),
//         QueryKeyFactory.module.cx.disconnectionRequests.summary(),
//         QueryKeyFactory.module.cx.disconnectionRequests.details(String(requestId)),
//       ],
//       onSuccess: () => {
//         toast({
//           title: "Disconnection request updated successfully!",
//           variant: "default",
//         });
//       },
//       onError: (error: any) => {
//         toast({
//           title: `Failed to update disconnection request: ${error.message}`,
//           variant: "destructive",
//         });
//       },
//     }
//   );
// };

export const useUpdateDisconnectionRequest = (requestId: number) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useSmartMutation(
    (payload: any) =>
      disconnectionApi.updateDisconnectionRequest(requestId, payload),
    {
      onSuccess: async () => {
        toast({
          title: "Success!",
          description: "Disconnection request updated successfully!",
        });

        // ✅ Refetch all related queries
        await queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.disconnectionRequests.details(
            String(requestId)
          ),
        });

        await queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.disconnectionRequests.list(),
        });

        await queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.disconnectionRequests.summary(),
        });

        await queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.disconnectionRequests.details(
            String(requestId)
          ),
        });
      },

      onError: (error: any) => {
        toast({
          title: "Error",
          description:
            error?.message || "Failed to update disconnection request",
          variant: "destructive",
        });
      },
    }
  );
};

/**
 * Delete disconnection request
 */
export const useDeleteDisconnectionRequest = () => {
  return useSmartMutation(
    (requestId: number) =>
      disconnectionApi.deleteDisconnectionRequest(requestId),
    {
      invalidateKeys: [
        QueryKeyFactory.module.cx.disconnectionRequests.list(),
        QueryKeyFactory.module.cx.disconnectionRequests.summary(),
      ],
      onSuccess: () => {
        toast({
          title: "Disconnection request deleted successfully!",
          variant: "default",
        });
      },
      onError: (error: any) => {
        toast({
          title: `Failed to delete disconnection request: ${error.message}`,
          variant: "destructive",
        });
      },
    }
  );
};

// ================================
// BILL GENERATION HOOKS
// ================================

/**
 * Create bill generation
 */
export const useCreateBillGeneration = (requestId: number) => {
  return useSmartMutation(
    (payload: CreateBillGenerationPayload) =>
      disconnectionApi.createBillGeneration(payload),
    {
      invalidateKeys: [
        QueryKeyFactory.module.cx.disconnectionRequests.consumerBill(),
        QueryKeyFactory.module.cx.disconnectionRequests.details(
          requestId?.toString()
        ),
      ],
      onSuccess: () => {
        toast({
          title: "Bill generation created successfully!",
          variant: "default",
        });
      },
      onError: (error: any) => {
        toast({
          title: `Failed to create bill generation: ${error.message}`,
          variant: "destructive",
        });
      },
    }
  );
};

/**
 * Update bill generation
 */
export const useUpdateBillGeneration = (billId: number) => {
  return useSmartMutation(
    ({
      billId,
      payload,
    }: {
      billId: number;
      payload: UpdateBillGenerationPayload;
    }) => disconnectionApi.updateBillGeneration(billId, payload),
    {
      invalidateKeys: [
        QueryKeyFactory.module.cx.disconnectionRequests.consumerBill(),
        QueryKeyFactory.module.cx.disconnectionRequests.details(
          billId.toString()
        ),
      ],
      onSuccess: () => {
        toast({
          title: "Bill generation updated successfully!",
          variant: "default",
        });
      },
      onError: (error: any) => {
        toast({
          title: `Failed to update bill generation: ${error.message}`,
          variant: "destructive",
        });
      },
    }
  );
};

/**
 * Approve single bill
 */
export const useApproveSingleBill = (requestId: number) => {
  return useSmartMutation(
    ({
      billId,
      payload,
    }: {
      billId: number;
      payload: SingleBillApprovePayload;
    }) => disconnectionApi.approveSingleBill(billId, payload),
    {
      invalidateKeys: [
        QueryKeyFactory.module.cx.disconnectionRequests.consumerBill(),
        QueryKeyFactory.module.cx.disconnectionRequests.details(
          requestId.toString()
        ),
      ],
      onSuccess: () => {
        toast({
          title: "Bill approved successfully!",
          variant: "default",
        });
      },
      onError: (error: any) => {
        toast({
          title: `Failed to approve bill: ${error.message}`,
          variant: "destructive",
        });
      },
    }
  );
};

/**
 * Disconnect consumer support
 */
export const useDisconnectConsumerSupport = (requestId: number) => {
  return useSmartMutation(
    ({
      consumerId,
      payload,
    }: {
      consumerId: number;
      payload: ConsumerSupportDisconnectPayload;
    }) => disconnectionApi.disconnectConsumerSupport(consumerId, payload),
    {
      invalidateKeys: [
        QueryKeyFactory.module.cx.disconnectionRequests.details(
          requestId.toString()
        ),
        QueryKeyFactory.module.cx.disconnectionRequests.list(),
        QueryKeyFactory.module.cx.disconnectionRequests.consumerBill(),
      ],
      onSuccess: () => {
        toast({
          title: "Consumer disconnected successfully!",
          variant: "default",
        });
      },
      onError: (error: any) => {
        toast({
          title: `Failed to disconnect consumer: ${error.message}`,
          variant: "destructive",
        });
      },
    }
  );
};

export const useDisconnectionStatus = (params: {
  remoteUtilityId: number;
  tab: number;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.disconnectionRequests.status(
      params.remoteUtilityId,
      params.tab
    ), // Include params in query key for caching
    () => disconnectionApi.getStatus(params)
  );
};

// Hook for setup progress (original)
export const useDisconnectionCategory = (params: {
  remoteUtilityId: number;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.disconnectionRequests.category(), // Include params in query key for caching
    () => disconnectionApi.getCategory(params)
  );
};

/**
 * Get utility request configuration
 */
export const useUtilityRequestConfiguration = (
  filters: UtilityConfigFilters
) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.disconnectionRequests.utilityConfig(filters),
    () => disconnectionApi.getUtilityRequestConfiguration(filters),
    {
      enabled: !!filters.requestType,
      staleTime: 10 * 60 * 1000,
    }
  );
};

/**
 * Get time slot choices
 */
export const useTimeSlotChoices = () => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.disconnectionRequests.timeSlotChoices(),
    () => disconnectionApi.getTimeSlotChoices(),
    {
      staleTime: 30 * 60 * 1000, // 30 minutes
    }
  );
};

/**
 * Get time slot choices
 */
export const useBillTemplates = (remoteUtilityId: number) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.disconnectionRequests.billTemplates(),
    () => disconnectionApi.getBillTemplates(remoteUtilityId),
    {
      staleTime: 30 * 60 * 1000, // 30 minutes
    }
  );
};

export const useMeterReadings = (filters: MeterReadingsFilters) => {
  const { startDate, endDate } = filters;

  return useSmartQuery(
    QueryKeyFactory.module.cx.disconnectionRequests.meterReadings(filters),
    () => disconnectionApi.getMeterReadings(filters),
    {
      enabled: Boolean(startDate && endDate),
      staleTime: 30 * 60 * 1000, // 30 minutes
    }
  );
};

export const useSubmitConsumerSelection = () => {
  const { toast } = useToast();
  return useSmartMutation(
    async (payload: any) => await submitConsumerSelection(payload),
    {
      onSuccess: async (_, payload) => {
        // ✅ Assuming requestId is available in the payload or response
        const requestId = payload?.requestId;

        if (requestId) {
          await queryClient.refetchQueries({
            queryKey: QueryKeyFactory.module.cx.disconnectionRequests.details(
              String(requestId)
            ),
          });
        }

        toast({
          title: "Success!",
          description: "Consumer selection submitted successfully.",
        });
      },

      onError: (error: any) => {
        toast({ title: "Error while creating the request" });
      },
    }
  );
};

export const usePrintBill = () => {
  return useSmartMutation(async (billId: string) => {
    const blob = await downloadBillTemplate(billId);
    printPDFBlob(blob);
    return blob;
  });
};

export const useApproveBill = (billId: any, billDraftId: number) => {
  const { toast } = useToast();

  return useSmartMutation(() => approveBill(billId, billDraftId), {
    onError: (error: any) => {
      toast({ title: "Error While Approving the Bill" });
    },
  });
};

export const useApproveDisconnectionBill = (billId: any, requestID: number) => {
  const { toast } = useToast();

  return useSmartMutation(() => approveDiscoonnectionBill(billId, requestID), {
    onSuccess: () => {
      // ✅ Invalidate disconnection details query after approval
      queryClient.invalidateQueries({
        queryKey: QueryKeyFactory.module.cx.disconnectionRequests.details(
          String(requestID)
        ),
      });

      // ✅ Also invalidate consumerBill query without filters
      queryClient.invalidateQueries({
        queryKey: ["cx", "disconnectionRequests", "consumerBill"],
      });
    },
    onError: (error: any) => {
      toast({ title: "Error While Approving the Bill" });
    },
  });
};

export const useRejectBill = (billId: any) => {
  const { toast } = useToast();

  return useSmartMutation(() => rejectBill(billId), {
    onError: (error: any) => {
      toast({ title: "Error While Approving the Bill" });
    },
  });
};

export const useUpdateDisconnection = (requestId: number) => {
  const navigate = useNavigate();
  return useSmartMutation(
    ({ requestId, payload }: { requestId: number; payload: any }) =>
      updateDisconnection(requestId, payload),

    {
      invalidateKeys: [
        QueryKeyFactory.module.cx.disconnectionRequests.details(
          String(requestId)
        ),
        QueryKeyFactory.module.cx.disconnectionRequests.list(),
        QueryKeyFactory.module.cx.disconnectionRequests.summary(),
      ],
      onSuccess: async () => {
        toast({ title: "Disconnection request updated successfully!" });
        queryClient.refetchQueries({
          queryKey: ["cx", "disconnectionRequests"],
        });
        queryClient.refetchQueries({
          queryKey: ["cx", "disconnectionRequests", "list"],
        });
        await queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.cx.disconnectionRequests
            .details(String(requestId))
            .slice(0, 3),
          exact: false,
        });
        navigate(`/cx/disconnections/?tab=history`);
      },
      onError: (error: any) => {
        toast({
          title: `Failed to update disconnection request: ${error.message}`,
        });
      },
    }
  );
};

export const useWorkOrderDetails = (search: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.disconnectionRequests.workOrderDetails(search),
    () => getWorkOrderDetails(search),
    {
      enabled: !!search,
    }
  );
};

export const useMeterInfo = (deviceNo: string, utilityService: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.disconnectionRequests.meterInfo(
      deviceNo,
      utilityService
    ),
    () => getMeterInfo(deviceNo, utilityService),
    {
      enabled: !!deviceNo,
    }
  );
};

export const useMeterHistory = (consumerId: number, action: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.disconnectionRequests.meterHistory(consumerId),
    () => getMeterHistory(consumerId),
    {
      enabled: !!consumerId && action === "history",
    }
  );
};
